// packages/web-ui/src/state/diff.ts
//
// Story W4-S7 — `diff` zustand slice. Owns the Diff destination's
// changeset-list state (active + history) plus the active-history
// classification selectors.
//
// SCOPE LIMIT: this slice is additive-only and does NOT touch the
// existing `changeset` slice from W4-S4 (state/changeset.ts). The diff
// slice has a single responsibility: enumerate changesets via
// `GET /api/v1/changesets` for the ContextPanel + history list.
//
// SSE wiring: W4-S4 owns the per-entity / per-changeset SSE handlers
// on the `changeset` slice. To avoid touching the W4-S4-frozen surface,
// the Diff destination subscribes its DiffMode component to the
// changeset slice's `changesetsById` Object.keys length and triggers
// `loadChangesetList()` whenever the count changes (debounced 500 ms).

import { createStore, type StoreApi } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type { ChangesetSummary } from '@tessa-lean/base';

import { changesetApi as defaultChangesetApi, type ChangesetApi } from '../api/clients/changeset.js';
import type { ApiContext } from '../api/client.js';

/** State fields owned by this slice. */
export interface DiffSliceState {
  /** All changesets known to the server, both active and historical. Sorted createdAt-desc by the server. */
  changesetSummariesList: ChangesetSummary[];
  /** Loading flag for the list fetch. Used to gate Empty / Loading / Error states. */
  changesetSummariesLoading: boolean;
  /** Last error from list fetch (cleared on next successful fetch). */
  changesetSummariesError: string | null;
}

export const DIFF_INITIAL_STATE: DiffSliceState = {
  changesetSummariesList: [],
  changesetSummariesLoading: false,
  changesetSummariesError: null,
};

/** Action surface owned by this slice. */
export interface DiffSliceActions {
  /**
   * Fetch `GET /api/v1/changesets` and populate `changesetSummariesList`.
   * Idempotent: no-op if a fetch is already in flight.
   */
  loadChangesetList: () => Promise<void>;
  /**
   * Test/debug helper — reset to defaults. Per-slice prefixed (MED-2) so the
   * RootStore composition no longer collides on a shared `_reset` name.
   */
  _resetDiff: () => void;
}

export type DiffSlice = DiffSliceState & DiffSliceActions;

/** Override hooks for the slice — used by tests to inject an api stub. */
export interface DiffSliceConfig {
  api?: ChangesetApi;
  apiContext?: ApiContext;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/**
 * zustand StateCreator — composes into a root store via the slice
 * pattern, OR can be wrapped in a vanilla store via {@link createDiffStore}.
 */
export const createDiffSlice =
  (config: DiffSliceConfig = {}): StateCreator<DiffSlice> =>
  (set, get) => {
    const api = config.api ?? defaultChangesetApi;
    const ctx = config.apiContext ?? defaultApiContext();
    return {
      ...DIFF_INITIAL_STATE,

      loadChangesetList: async () => {
        if (get().changesetSummariesLoading) return;
        set({ changesetSummariesLoading: true, changesetSummariesError: null });
        const res = await api.listChangesets(ctx);
        if (res.ok) {
          set({
            changesetSummariesList: res.value.changesets,
            changesetSummariesLoading: false,
            changesetSummariesError: null,
          });
        } else {
          set({
            changesetSummariesError: res.error.message,
            changesetSummariesLoading: false,
          });
        }
      },

      _resetDiff: () => {
        set({ ...DIFF_INITIAL_STATE });
      },
    };
  };

/** Vanilla-store factory for standalone use (tests, non-shell call-sites). */
export function createDiffStore(config: DiffSliceConfig = {}): StoreApi<DiffSlice> {
  return createStore<DiffSlice>()(createDiffSlice(config));
}

// ─── Selectors ──────────────────────────────────────────────────────────────

/** Active changesets — anything not in a fully terminal state. */
export function selectActiveChangesets(s: DiffSliceState): ChangesetSummary[] {
  return s.changesetSummariesList.filter((c) => c.aggregateStatus === 'active');
}

/** History changesets — fully applied / rejected / partial. */
export function selectHistoryChangesets(s: DiffSliceState): ChangesetSummary[] {
  return s.changesetSummariesList.filter((c) => c.aggregateStatus !== 'active');
}

/** Search filter — case-insensitive substring over id + intent. */
export function filterChangesetsByQuery(
  list: ChangesetSummary[],
  query: string,
): ChangesetSummary[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return list;
  return list.filter((c) => {
    if (c.id.toLowerCase().includes(q)) return true;
    if (c.intent.toLowerCase().includes(q)) return true;
    if (c.fromSessionTitle !== null && c.fromSessionTitle.toLowerCase().includes(q)) return true;
    if (c.createdAt.toLowerCase().includes(q)) return true;
    return false;
  });
}
